// Effective-age resolution + the single "little ones / big ones" threshold (Aug 2026).
//
// Why this exists: child.age was a number typed in once and never updated, so every
// age-adaptive subsystem slowly drifted as children grew. The fix deliberately collects ZERO
// new personal data (privacy review, Aug 2026 — MM/YYYY and school-year capture were both
// considered; school year landed later, Aug 2026, per Money Quest scope §3a — see
// SCHOOL_YEAR_OPTIONS/estimateSchoolYear/resolveSchoolYear below): we keep the plain age and
// simply stamp WHEN it was set (child.ageSetAt, a local "YYYY-MM-DD" via todayStr()'s
// format). Effective age = stored age + full years elapsed since the stamp — i.e. we assume
// the entry date is the birthday, which is at most a year out and self-corrects every time a
// parent edits the age (the edit UI shows effective age and every save restamps, so a
// no-change save is harmlessly correct rather than a clock rewind — see ProfilesTab.jsx).
//
// This module is deliberately dependency-free (imports nothing) so ANY other module — libs
// like oinks.js/maths.js and components alike — can import it without cycle risk. The
// lib→lib import precedent is snackCatch.js → maths.js.

// The one age at which everything switches from the "little ones" treatment to the grown-up
// one (Alan's consolidation decision, Aug 2026 — previously the mascot name and Snack Attack
// Gentle mode split at 7 while the colour theme split at 9). Under 8: "Piggles", Gentle
// arcade mode, candy theme, "Altogether I have" on the hero strip. 8 and up (and missing /
// non-numeric ages — the established safe default for an unknown, likely older, child):
// "Piggington", Standard mode, mint theme, "Total wealth".
export const AGE_UP_THRESHOLD = 8;

// The child's age as of TODAY: stored age + full years elapsed since ageSetAt. Defensive on
// every field (preview-mode profiles never pass through migrateState; pre-feature profiles
// have no ageSetAt at all): missing/non-numeric age → null (callers already handle null —
// same contract as the old raw child.age reads); missing or malformed ageSetAt → the stored
// age as-is, exactly the pre-feature behaviour. Elapsed years never go negative, so a
// device with a badly wrong clock (or a future-dated stamp) can't make a child younger.
export function effectiveAge(child) {
  const base = child && typeof child.age === "number" ? child.age : null;
  if (base === null) return null;
  const setAt = child && typeof child.ageSetAt === "string" ? child.ageSetAt : null;
  if (!setAt || !/^\d{4}-\d{2}-\d{2}$/.test(setAt)) return base;
  const [y, m, d] = setAt.split("-").map(Number);
  // Local-date comparison, same convention as utils.js's todayStr() — a birthday-ish
  // anniversary check without Date-object timezone traps: a full year has elapsed once
  // today's month/day reaches the stamp's month/day.
  const now = new Date();
  let years = now.getFullYear() - y;
  const mm = now.getMonth() + 1;
  const dd = now.getDate();
  if (mm < m || (mm === m && dd < d)) years -= 1;
  return base + Math.max(0, years);
}

// True only for a KNOWN under-threshold child — the "little ones" test used by the hero
// strip's wealth label (ChildHero.jsx) and available to any future copy split. Null/unknown
// age is NOT young (the safe older default), which is why this can't be `!isOld(...)`.
export function isYoungChild(child) {
  const age = effectiveAge(child);
  return typeof age === "number" && age < AGE_UP_THRESHOLD;
}

// School Year (Aug 2026 — Money Quest scope §3a, the "deferred to Money Quest scope" this
// file's own top comment promised). Still zero-new-data by default in the sense that
// matters: a profile's schoolYear stays null until a parent EXPLICITLY sets it via
// ProfilesTab — nothing here writes anything. What this adds is a live ESTIMATE so an
// unconfirmed profile is never blank, computed fresh from effectiveAge every time rather
// than stored, so it drifts forward correctly on its own for any child a parent never
// touches this for.
//
// Why bother when age already exists: every UK school year turns over on the same day, 1
// September, regardless of where an individual child's birthday falls — age alone can be
// up to eleven months out of step with what a child is actually being taught. School year
// is the more precise signal, and the whole reason this file couldn't just keep guessing
// bands from age forever.
export const SCHOOL_YEAR_OPTIONS = ["Pre-school", "Reception", "Y1", "Y2", "Y3", "Y4", "Y5", "Y6", "Y7", "Y8", "Y9", "Y10"];

// age -> school year, current-to-upcoming September (formula confirmed Aug 2026). Capped at
// Y10 for any effective age of 15+ — matches this app's general "still just track them,
// don't try to model exact secondary-school stages we have no other signal for" posture.
export function estimateSchoolYear(age) {
  if (typeof age !== "number" || age < 0) return null;
  if (age <= 3) return "Pre-school";
  if (age === 4) return "Reception";
  if (age <= 14) return `Y${age - 4}`;
  return "Y10";
}

// The value any consumer should actually show or band against: a parent's own confirmed
// choice if one exists, otherwise the live estimate from effectiveAge — so a caller never
// has to duplicate the null-check-then-estimate dance itself. Callers that need to show the
// "(estimated)" distinction (ProfilesTab) should still read child.schoolYear directly
// rather than inferring it from this, since this function can't tell an unset profile from
// one a parent explicitly set to the same value estimation would have picked anyway.
export function resolveSchoolYear(child) {
  if (child && typeof child.schoolYear === "string" && child.schoolYear) return child.schoolYear;
  return estimateSchoolYear(effectiveAge(child));
}
